import styled, { css, keyframes } from 'styled-components';

import { ToastType } from './toast-theme';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

export const ToastContainer = styled.div<{ position: string; isHovered: boolean }>`
  position: fixed;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;

  ${({ position }) => {
    switch (position) {
      case 'top-center':
        return css`
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          align-items: center;
        `;
      case 'top-right':
        return css`
          top: 20px;
          right: 20px;
          align-items: flex-start;
        `;
      case 'bottom-right':
        return css`
          bottom: 20px;
          right: 20px;
          align-items: flex-start;
        `;
      default:
        return css`
          top: 20px;
          right: 20px;
          align-items: flex-start;
        `;
    }
  }}
`;

export const ToastStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  align-items: flex-start;
  pointer-events: all;
`;

export const ToastItem = styled.div<{
  type: ToastType;
  isVisible: boolean;
  isExpanded: boolean;
  index: number;
  totalVisible: number;
}>`
  position: relative;
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 300px;
  max-width: 400px;
  box-shadow: ${({ theme }) => theme.toast.shadow || '0px 4px 16px 0px rgba(0, 0, 0, 0.15)'};
  pointer-events: all;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  animation: ${slideIn} 0.2s ease-out;
  overflow: visible;

  /* Base background - white for light theme, dark for dark theme */
  background-color: ${({ theme }) => {
    return theme.toast.backgroundColor;
  }};

  ${({ type, theme }) => {
    const colorMap = {
      info: theme.toast.infoBorderColor,
      success: theme.toast.successBorderColor,
      warning: theme.toast.warningBorderColor,
      error: theme.toast.errorBorderColor,
      default: theme.toast.defaultBorderColor,
    };

    const borderColor = colorMap[type];
    // Convert hex to rgba with 50% opacity for overlay
    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return css`
      border: 1px solid ${borderColor};

      /* Colored overlay with 50% opacity */
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${hexToRgba(borderColor, 0.5)};
        pointer-events: none;
        z-index: 0;
      }

      /* Ensure content is above the overlay */
      > * {
        position: relative;
        z-index: 1;
      }
    `;
  }}

  ${({ isExpanded, index, totalVisible }) => {
    if (isExpanded) {
      // Fully visible when expanded
      return css`
        opacity: 1;
        transform: translateY(0);
        margin-bottom: 8px;
      `;
    }

    // Stacked state - vertical only, no horizontal offset
    const lastIndex = totalVisible - 1;
    return css`
      opacity: ${index === lastIndex ? 1 : 0.7};
      transform: translateY(0);
      margin-bottom: 8px;
    `;
  }}

  &:hover {
    ${({ type, theme }) => {
      const colorMap = {
        info: theme.toast.infoBorderColor,
        success: theme.toast.successBorderColor,
        warning: theme.toast.warningBorderColor,
        error: theme.toast.errorBorderColor,
        default: theme.toast.defaultBorderColor,
      };

      const borderColor = colorMap[type];
      // Convert hex to rgba with 60% opacity for hover overlay
      const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      return css`
        &::before {
          background-color: ${hexToRgba(borderColor, 0.6)};
        }
      `;
    }}
    transform: scale(1.02);
    z-index: ${({ index }) => 10000 + index};
  }

  &.exiting {
    animation: ${slideOut} 0.2s ease-in forwards;
  }
`;

export const ToastContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const ToastIcon = styled.div<{ type: ToastType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.toast.iconBackgroundColor};
  flex-shrink: 0;
  color: ${({ theme, type }) => {
    const colorMap = {
      info: theme.toast.infoBorderColor,
      success: theme.toast.successBorderColor,
      warning: theme.toast.warningBorderColor,
      error: theme.toast.errorBorderColor,
      default: theme.toast.iconColor,
    };
    return colorMap[type];
  }};
`;

export const ToastMessage = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const ToastTitle = styled.div`
  font-family: 'Open Sans';
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;
  color: ${({ theme }) => theme.toast.titleColor};
`;

export const ToastDescription = styled.div`
  font-family: 'Open Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  color: ${({ theme }) => theme.toast.textColor};
  word-wrap: break-word;
`;

export const ToastCloseButton = styled.button`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: ${({ theme }) => {
    return theme.toast.backgroundColor;
  }};
  border: 1px solid ${({ theme }) => theme.toast.defaultBorderColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  color: ${({ theme }) => theme.toast.closeButtonColor};
  border-radius: 50%;
  flex-shrink: 0;
  z-index: 10;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
`;

export const ToastMoreButton = styled.div<{
  type: ToastType;
  isVisible: boolean;
  isExpanded: boolean;
  index: number;
  totalVisible: number;
  onClick?: () => void;
}>`
  position: relative;
  border-radius: 8px;
  padding: 6px 12px;
  min-width: 120px;
  max-width: 200px;
  text-align: center;
  font-family: 'Open Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  pointer-events: all;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  overflow: hidden;

  /* Base background - white for light theme, dark for dark theme */
  background-color: ${({ theme }) => {
    return theme.toast.backgroundColor;
  }};

  color: ${({ theme }) => theme.toast.textColor};

  ${({ type, theme }) => {
    const colorMap = {
      info: theme.toast.infoBorderColor,
      success: theme.toast.successBorderColor,
      warning: theme.toast.warningBorderColor,
      error: theme.toast.errorBorderColor,
      default: theme.toast.defaultBorderColor,
    };

    const borderColor = colorMap[type];
    // Convert hex to rgba with 50% opacity for overlay
    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return css`
      border: 1px solid ${borderColor};

      /* Colored overlay with 50% opacity */
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${hexToRgba(borderColor, 0.5)};
        pointer-events: none;
        z-index: 0;
      }

      /* Ensure content is above the overlay */
      > * {
        position: relative;
        z-index: 1;
      }
    `;
  }}

  ${({ isExpanded, index, totalVisible }) => {
    if (isExpanded) {
      return css`
        opacity: 1;
        transform: translateY(0);
      `;
    }

    const lastIndex = totalVisible - 1;
    return css`
      opacity: ${index === lastIndex ? 1 : 0.7};
      transform: translateY(0);
    `;
  }}

  &:hover {
    ${({ type, theme }) => {
      const colorMap = {
        info: theme.toast.infoBorderColor,
        success: theme.toast.successBorderColor,
        warning: theme.toast.warningBorderColor,
        error: theme.toast.errorBorderColor,
        default: theme.toast.defaultBorderColor,
      };

      const borderColor = colorMap[type];
      const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      return css`
        &::before {
          background-color: ${hexToRgba(borderColor, 0.6)};
        }
      `;
    }}
    transform: scale(1.02);
  }
`;

export const ToastActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  width: 100%;
  pointer-events: all;
`;

export const ToastCloseAllButton = styled.button`
  border: none;
  background-color: ${({ theme }) => {
    return theme.toast.backgroundColor;
  }};
  border: 1px solid ${({ theme }) => theme.toast.defaultBorderColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  color: ${({ theme }) => theme.toast.closeButtonColor};
  transition: all 0.2s ease;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);

  &:hover {
    color: ${({ theme }) => theme.toast.closeButtonHoverColor};
    background-color: ${({ theme }) => theme.toast.hoverBackgroundColor};
    transform: scale(1.1);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.toast.infoBorderColor};
    outline-offset: 2px;
  }
`;
